/* =========================================================================
 * Kkumi · Cart shipping panel (sections/kkumi-cart-shipping.liquid)
 * -------------------------------------------------------------------------
 * Renders the free-shipping message and progress bar(s) for the chosen
 * country, and re-renders on every cart change (Dawn's `cartUpdate`
 * publish carries the new cart; otherwise /cart.js is read).
 *
 * Amounts: thresholds are subunits ×100 in the SHOP currency; the cart total
 * is in the shopper's currency, so it is converted with KKMoney.toShopCents
 * before comparing and the gaps are shown back in the shopper's currency with
 * KKMoney.fromShopCents (an estimate — Shopify.currency.rate — where a market
 * has its own fixed prices). No usable rate → no message, no bar.
 * All copy is section settings on data-*; values are escaped before
 * innerHTML (KKEsc); the money string is bolded by the template.
 *
 * It also drives the order summary's Shipping row: Free / Free express at a
 * threshold, the flat standard rate (au_rate / nz_rate) below it, otherwise
 * "At checkout". The flat rate is announced as `kkc:shipping` { ship, cents }
 * (cents in the shopper's currency; also on window.KKCartShip) so the
 * summary adds it to the Total. A member's free-shipping code on the order
 * that covers the flat rate (its countries and rate cap, which the order
 * summary lists on window.KKCartShipCodes and announces as `kkc:ship-codes`)
 * makes it "Free at checkout" (ship 'code', nothing added): Shopify takes the
 * rate off at checkout.
 * ========================================================================= */
(function () {
  'use strict';
  if (window.__kkcShipInit) return;
  window.__kkcShipInit = true;

  var COUNTRY_KEY = 'kkcShipCountry';

  function num(v) { var n = parseInt(v, 10); return isFinite(n) ? n : null; }
  // A shop-currency amount shown in the shopper's currency (the shop's own
  // label when there is no usable rate).
  function money(c) { return window.KKMoney.fromShopCents(c, { trim: true }); }
  var esc = function (v) { return window.KKEsc(v); };
  function remembered() { try { return window.sessionStorage.getItem(COUNTRY_KEY); } catch (e) { return null; } }
  function remember(v) { try { window.sessionStorage.setItem(COUNTRY_KEY, v); } catch (e) {} }
  function codes(v) { return String(v || '').toUpperCase().split(',').filter(Boolean); }

  // "US: 150, 250" per line -> { US: { std, exp } } in shop-currency subunits.
  function parseRules(text) {
    var out = {};
    String(text || '').split(/[\n;]+/).forEach(function (line) {
      var m = /^\s*([A-Za-z]{2})\s*[:=]\s*(\d+(?:\.\d+)?)(?:\s*[,\/]\s*(\d+(?:\.\d+)?))?\s*$/.exec(line);
      if (m) out[m[1].toUpperCase()] = { std: Math.round(parseFloat(m[2]) * 100), exp: m[3] ? Math.round(parseFloat(m[3]) * 100) : null };
    });
    return out;
  }

  // Template -> HTML: tokens are replaced with escaped values; [gap]/[au]/[nz]
  // amounts are bolded.
  function tpl(text, map) {
    var out = esc(text || '');
    Object.keys(map).forEach(function (k) {
      var v = map[k];
      // [to] is a suffix: Shopify trims saved text, so a leading space in the
      // setting can't be relied on — add one unless it starts with punctuation.
      var html = (k === 'to') ? (/^[\s,.;:]/.test(v || '') || !v ? esc(v) : ' ' + esc(v)) : '<b>' + esc(v) + '</b>';
      out = out.split('[' + k + ']').join(html);
    });
    return out;
  }

  function bar(label, max, now, thin) {
    var pct = max > 0 ? Math.max(2, Math.min(100, now / max * 100)) : 100;
    return '<div class="kkc-ship__bar' + (thin ? ' kkc-ship__bar--thin' : '') + '" role="progressbar" aria-label="' + esc(label) + '"' +
      ' aria-valuemin="0" aria-valuemax="' + (max / 100).toFixed(2) + '" aria-valuenow="' + (Math.min(now, max) / 100).toFixed(2) + '">' +
      '<span class="kkc-ship__fill" style="width:' + pct.toFixed(1) + '%"></span></div>';
  }

  // The country's display name: the list's own spelling, else the one the
  // section rendered for the detected country.
  function nameOf(root, code) {
    var tpl = root.querySelector('template[data-kkc-ship-sheet]');
    if (tpl && tpl.content) {
      var b = tpl.content.querySelector('[data-code="' + code + '"]');
      if (b) return b.getAttribute('data-name') || b.textContent;
    }
    return code === String(root.dataset.countryCode || '').toUpperCase() ? (root.dataset.countryName || '') : '';
  }

  function render(root) {
    var d = root.dataset;
    var code = String(d.picked || d.countryCode || '').toUpperCase();
    var name = code ? nameOf(root, code) : '';

    var whereEl = root.querySelector('[data-kkc-ship-where]');
    var whereText = name || d.tNone || '';
    if (whereEl && whereEl.textContent !== whereText) whereEl.textContent = whereText;

    var msgEl = root.querySelector('[data-kkc-ship-msg]');
    var barsEl = root.querySelector('[data-kkc-ship-bars]');
    // Cart total + signature line are in the shopper's currency; the
    // thresholds are shop-currency settings, so the maths runs in the shop's
    // currency (toShopCents is a no-op for a shop-currency shopper, null when a
    // foreign currency has no usable rate — then no message, never a guess).
    var qual = window.KKMoney.toShopCents(Math.max(0, (num(d.total) || 0) - (num(d.sigCents) || 0)));
    var msg = '';
    var bars = '';
    var ship = 'later'; // the summary's Shipping row: free | express | flat | later
    var auStd = num(d.auStd), nzStd = num(d.nzStd);

    // The country's rule: Australia / New Zealand use their own settings, any
    // other country its other_rules line (none = "calculated at checkout").
    var rule = null, to = '';
    if (code && codes(d.auCodes).indexOf(code) >= 0) rule = { std: auStd, exp: num(d.auExp), rate: num(d.auRate) };
    else if (code && codes(d.nzCodes).indexOf(code) >= 0) { rule = { std: nzStd, exp: num(d.nzExp), rate: num(d.nzRate) }; to = d.tNz || ''; }
    else if (code) {
      if (!root.__kkcRules) root.__kkcRules = parseRules(d.otherRules);
      rule = root.__kkcRules[code] || null;
      if (rule) to = String(d.tCountry || '').split('[country]').join(name);
    }

    if (qual == null) {
      msg = '';
    } else if (!code) {
      msg = '<span>' + tpl(d.tUnknown, { au: money(auStd || 0), nz: money(nzStd || 0) }) + '</span>';
    } else if (!rule) {
      msg = '<span>' + esc(d.tOther) + '</span>';
    } else {
      var std = rule.std == null ? 0 : rule.std;
      var exp = rule.exp;
      if (qual < std) {
        msg = '<span>' + tpl(d.tBelow, { gap: money(std - qual), to: to }) + '</span>';
        bars = bar(d.tBarStd, std, qual, false);
      } else if (exp && qual < exp) {
        msg = '<span class="kkc-ok">' + tpl(d.tStd, { to: to }) + '</span>' +
          '<span class="kkc-mute">' + tpl(d.tExpMore, { gap: money(exp - qual) }) + '</span>';
        bars = bar(d.tBarExp, exp, qual, true);
      } else if (exp) {
        msg = '<span class="kkc-ok">' + esc(d.tExp) + '</span>';
        ship = 'express';
      } else {
        msg = '<span class="kkc-ok">' + tpl(d.tStd, { to: to }) + '</span>';
      }
      if (qual >= std && ship !== 'express') ship = 'free';
    }
    // Below free standard in a flat-rate zone: the rate is the shipping cost
    // (shop currency, shown and added in the shopper's — no usable rate means
    // no estimate, so "At checkout").
    var rate = rule && rule.rate != null && rule.rate > 0 ? rule.rate : null;
    var shipCents = 0;
    if (ship === 'later' && qual != null && rate != null) {
      var local = window.KKMoney.toLocalCents(rate);
      if (local != null) { ship = 'flat'; shipCents = local; }
    }
    if (ship === 'flat' && coveredByCode(code, rate)) { ship = 'code'; shipCents = 0; }
    if (msgEl && msgEl.innerHTML !== msg) msgEl.innerHTML = msg;
    if (msgEl) msgEl.hidden = !msg;
    if (barsEl && barsEl.innerHTML !== bars) barsEl.innerHTML = bars;
    root.dataset.ship = ship;
    syncSummary(ship, shipCents);
  }

  // A member's free-shipping code on the order that covers this destination's
  // flat rate (shop-currency cents, like the code's cap).
  function coveredByCode(code, rate) {
    return (window.KKCartShipCodes || []).some(function (x) {
      return x && x.cap > 0 && rate <= x.cap && (x.countries || []).indexOf(code) !== -1;
    });
  }

  // The order summary's Shipping row follows the chosen country, and the
  // summary's Total picks up a flat rate from `kkc:shipping`.
  function syncSummary(ship, cents) {
    document.querySelectorAll('[data-kkc-ship-value]').forEach(function (el) {
      var html;
      // Flat rate: the amount bold, the words around it ("Standard") muted.
      if (ship === 'flat') html = esc(el.dataset.flat || '[amount]').split('[amount]').map(function (part) {
        return part.trim() ? '<span class="kkc-row__note">' + part + '</span>' : part;
      }).join('<b>' + esc(window.KKMoney.fromCents(cents)) + '</b>');
      else {
        var label = ship === 'express' ? el.dataset.freeExpress : ship === 'free' ? el.dataset.free
          : ship === 'code' ? (el.dataset.freeCode || el.dataset.free) : el.dataset.later;
        html = ship === 'later' ? '<span class="kkc-mute">' + esc(label) + '</span>' : '<b>' + esc(label) + '</b>';
      }
      if (el.innerHTML !== html) el.innerHTML = html;
    });
    // Sent on every render: cart.js re-renders the summary's Total from Liquid
    // (no shipping) on each change, so the summary re-applies it each time.
    window.KKCartShip = { ship: ship, cents: cents };
    document.dispatchEvent(new CustomEvent('kkc:shipping', { detail: window.KKCartShip }));
  }

  // ---- The country sheet (the template in the section, shown as .kkc-dr) ----
  var sheet = null; // { host, root, trigger }

  function closeSheet(returnFocus) {
    if (!sheet) return;
    var s = sheet;
    sheet = null;
    document.removeEventListener('keydown', s.onKey, true);
    if (s.host.parentNode) s.host.parentNode.removeChild(s.host);
    document.documentElement.classList.remove('kkc-lock');
    if (returnFocus && s.trigger && s.trigger.isConnected) s.trigger.focus();
  }

  function sheetFocusables(host) {
    return Array.prototype.slice.call(host.querySelectorAll('.kkc-dr button, .kkc-dr input, .kkc-dr [tabindex="-1"]'))
      .filter(function (x) { return x.offsetParent !== null; });
  }

  function filterList(host, q) {
    var needle = String(q || '').trim().toLowerCase();
    var shown = 0;
    host.querySelectorAll('.kkc-cty').forEach(function (b) {
      var li = b.parentNode;
      var hide = !!needle && String(b.getAttribute('data-name') || '').toLowerCase().indexOf(needle) < 0;
      if (li.hidden !== hide) li.hidden = hide;
      if (!hide) shown++;
    });
    var empty = host.querySelector('[data-kkc-ship-empty]');
    if (empty && empty.hidden !== (shown > 0)) empty.hidden = shown > 0;
  }

  function openSheet(root, trigger) {
    var tpl = root.querySelector('template[data-kkc-ship-sheet]');
    if (!tpl || sheet) return;
    var host = document.createElement('div');
    host.className = 'kkc kkc-ship-sheet';
    host.appendChild(tpl.content.cloneNode(true));
    document.body.appendChild(host);
    document.documentElement.classList.add('kkc-lock');

    var current = String(root.dataset.picked || root.dataset.countryCode || '').toUpperCase();
    host.querySelectorAll('.kkc-cty').forEach(function (b) {
      var on = b.getAttribute('data-code') === current;
      if (on) b.setAttribute('aria-current', 'true');
    });

    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); closeSheet(true); return; }
      if (e.key !== 'Tab') return;
      var f = sheetFocusables(host);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKey, true);
    sheet = { host: host, root: root, trigger: trigger, onKey: onKey };

    host.addEventListener('click', function (e) {
      if (e.target.closest('[data-kkc-ship-close]')) { closeSheet(true); return; }
      var b = e.target.closest('.kkc-cty');
      if (!b) return;
      root.dataset.picked = b.getAttribute('data-code');
      remember(root.dataset.picked);
      var tg = sheet && sheet.trigger;
      closeSheet(false);
      render(root);
      if (tg && tg.isConnected) tg.focus();
    });
    host.addEventListener('input', function (e) {
      if (e.target.closest && e.target.closest('[data-kkc-ship-q]')) filterList(host, e.target.value);
    });

    requestAnimationFrame(function () {
      var cur = host.querySelector('.kkc-cty[aria-current="true"]');
      if (cur && cur.scrollIntoView) cur.scrollIntoView({ block: 'center' });
      var h = host.querySelector('.kkc-dr h2');
      if (h) h.focus({ preventScroll: true });
    });
  }

  function each(fn) { document.querySelectorAll('[data-kkc-ship]').forEach(fn); }

  function applyCart(cart) {
    if (!cart || cart.total_price == null) return;
    each(function (root) {
      var sigId = String(root.dataset.signatureId || '');
      var sig = 0;
      (cart.items || []).forEach(function (it) {
        if (sigId && String(it.variant_id) === sigId) sig += it.final_line_price || 0;
      });
      root.dataset.total = cart.total_price;
      root.dataset.sigCents = sig;
      render(root);
    });
  }

  function bind(root) {
    if (root.__kkcBound) return;
    root.__kkcBound = true;
    var saved = remembered();
    if (saved) root.dataset.picked = saved;
    root.addEventListener('click', function (e) {
      var ch = e.target.closest ? e.target.closest('[data-kkc-ship-change]') : null;
      if (ch) openSheet(root, ch);
    });
    render(root);
  }

  function init() {
    each(bind);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
  // The member's free-shipping codes on the order changed (the order summary).
  document.addEventListener('kkc:ship-codes', function () { each(render); });

  function subscribeCart() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var cart = event && event.cartData;
      if (cart && cart.total_price != null && cart.items) { applyCart(cart); return; }
      each(render);
      fetch(((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); }).then(applyCart).catch(function () {});
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', subscribeCart);
  else subscribeCart();
})();
